import { useState, useCallback } from "react";

import { clsx } from "clsx";
import { useSelector } from "react-redux";

import { RVLoading } from "@regal-voice/ui";
import { selectEditingJourneyDetails } from "Services/state/journeys/JourneyStateSlice";

import { JourneyActionsPanel } from "./Components/JourneyActionsPanel/JourneyActionsPanel";
import { JourneyDetailsModal } from "./Components/JourneyDetailsModal/JourneyDetailsModal";
import { FlowProvider } from "./Components/JourneyFlowEditor/FlowProvider";
// todo replace with shared version
// @see "Components/elements/RVFlow/RVFlow";
import { Flow } from "./Components/JourneyFlowEditor/FlowWrapper";
import { Toolbar } from "./Components/Toolbar";

import styles from "./JourneyBuilder.module.scss";

type JourneyBuilderProps = {
    saveJourney: (status: string, overrides: Record<string, any>) => Promise<void>;
    saving?: boolean;
    loading?: boolean;
    isViewOnly?: boolean;
    showExecutionMonitor?: boolean;
    onOpenExecutionMonitor?: () => void;
    onCloseExecutionMonitor?: () => void;
};

export function JourneyBuilder({
    saveJourney,
    saving = false,
    loading = false,
    isViewOnly = false,
    showExecutionMonitor = false,
    onOpenExecutionMonitor,
    onCloseExecutionMonitor,
}: JourneyBuilderProps): JSX.Element {
    const [expanded, setExpanded] = useState<boolean>(true);
    const expand = useCallback(() => setExpanded(true), [setExpanded]);
    const collapse = useCallback(() => setExpanded(false), [setExpanded]);
    const openExecutionMonitor = useCallback(() => {
        onOpenExecutionMonitor?.();
        setExpanded(true);
    }, [onOpenExecutionMonitor]);

    const editingJourneyDetails = useSelector(selectEditingJourneyDetails);

    return (
        <FlowProvider>
            {/* Destroy the modal when it's not shown helps reset its state */}
            {!!editingJourneyDetails && <JourneyDetailsModal saveJourney={saveJourney} saving={saving} />}
            <div className={clsx(styles.wrapper, "flex-column")}>
                <Toolbar />
                <div className={clsx(styles.container, "flex-row")}>
                    {loading ? (
                        <RVLoading text="Loading your journey..." />
                    ) : (
                        <Flow expand={expand} isViewOnly={isViewOnly} onViewExecutions={openExecutionMonitor} />
                    )}
                    <JourneyActionsPanel
                        onCollapse={collapse}
                        onExpand={expand}
                        expanded={expanded}
                        loading={loading}
                        isViewOnly={isViewOnly}
                        showExecutionMonitor={showExecutionMonitor}
                        onCloseExecutionMonitor={onCloseExecutionMonitor}
                    />
                </div>
            </div>
        </FlowProvider>
    );
}
